import { useState } from 'react';
import { ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { ProfileBadge } from '@/components/ui';
import { getChildrenFromEvent, type EventRow } from '@/lib/schedule';
import type { OngoingEvent, OngoingTint } from '@/lib/ongoing';

export interface OngoingSectionProps {
  /** Ranked ongoing events — already sorted by `buildOngoingEvents`. */
  rows: OngoingEvent[];
  /** Opens Event Details. Read-only at Can view; the section itself is identical. */
  onSelect: (event: EventRow) => void;
  className?: string;
}

/** Rows visible before the footer has to offer the rest. */
const COLLAPSED_LIMIT = 3;

/**
 * Tints per AC 5. Never green: on a deadline surface green reads as "you're
 * fine", which is the opposite of what a countdown is for. The label text
 * always carries the meaning on its own, so colour is reinforcement only.
 */
const TINT_CLASS: Record<OngoingTint, string> = {
  error: 'text-text-error',
  warning: 'text-warning',
  neutral: 'text-text-body',
};

/**
 * "All ongoing" — every unfinished multi-day event in the household, ranked by
 * what changes next (YAR-419).
 *
 * Membership and order are GLOBAL: browsing to another week re-scopes the
 * pinned span bars but never this list. That is the whole point of the
 * section — a long event is exactly the thing you cannot see from the week
 * you happen to be looking at.
 */
export function OngoingSection({
  rows,
  onSelect,
  className = '',
}: OngoingSectionProps) {
  const [expanded, setExpanded] = useState(false);

  // Zero state is no state: the section disappears rather than explaining
  // itself (AC 1). An empty card would cost a parent a scroll to learn
  // nothing.
  if (rows.length === 0) return null;

  const total = rows.length;
  const hasOverflow = total > COLLAPSED_LIMIT;
  const visible = expanded ? rows : rows.slice(0, COLLAPSED_LIMIT);

  return (
    <section
      aria-label="All ongoing"
      className={[
        'flex flex-col gap-3 items-start w-full rounded-xl border border-border-strong bg-background p-5',
        'shadow-[0px_0px_2px_rgba(56,64,74,0.06)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex items-center gap-1 w-full">
        <h2 className="text-base font-bold text-text-heading">All ongoing</h2>
        <span
          data-testid="ongoing-count"
          className="inline-flex min-w-6 items-center justify-center rounded-full border border-border-strong bg-background px-1 py-1 text-xs leading-4 text-text-body"
        >
          {total}
        </span>
      </div>

      {/* Expanded, the list is capped to the viewport and scrolls inside
          itself (AC 9) so a household with a dozen ongoing events can't push
          the day feed off the screen entirely. */}
      <div
        className={[
          'flex flex-col items-start w-full',
          expanded ? 'max-h-[60vh] overflow-y-auto' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {visible.map((row, i) => (
          <div
            key={row.event.id}
            className="w-full"
          >
            {i > 0 && <div className="h-px w-full bg-border" />}
            <OngoingRow
              row={row}
              onSelect={onSelect}
            />
          </div>
        ))}
      </div>

      {hasOverflow && (
        <div className="flex w-full items-center justify-center">
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="inline-flex items-center justify-center gap-1.5 rounded-md px-1.5 py-1 text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
          >
            {expanded ? 'Show less' : `See all (${total})`}
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      )}
    </section>
  );
}

function OngoingRow({
  row,
  onSelect,
}: {
  row: OngoingEvent;
  onSelect: (event: EventRow) => void;
}) {
  const children = getChildrenFromEvent(row.event);

  return (
    <button
      type="button"
      data-testid="ongoing-row"
      onClick={() => onSelect(row.event)}
      aria-label={`${row.event.title} — ${row.label}`}
      className="flex w-full flex-col items-start gap-1 py-2.5 text-left cursor-pointer bg-transparent border-none"
    >
      <div className="flex w-full items-center gap-2">
        {/* The title is the only thing allowed to truncate (AC 6) — a clipped
            state label would turn "3 days left" into a different claim. */}
        <span className="flex-1 min-w-0 truncate text-base font-medium text-text-heading">
          {row.event.title}
        </span>
        <span
          className={`shrink-0 text-sm font-medium ${TINT_CLASS[row.tint]}`}
        >
          {row.label}
        </span>
        <ChevronRight
          size={16}
          className="shrink-0 text-muted-foreground"
        />
      </div>

      {children.length > 0 && (
        <div className="flex items-center gap-1">
          {children.map((c) => (
            <span
              key={c.id}
              data-testid="ongoing-chip"
            >
              <ProfileBadge
                name={c.name}
                emoji={c.emoji_avatar ?? '🧒'}
                color={c.emoji_color ?? undefined}
                imagePath={c.avatar_image_path}
              />
            </span>
          ))}
        </div>
      )}
    </button>
  );
}
